import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SharedService } from '../../_services/shared.service';
import { ShoppingCartService } from '../../_services/shopping-cart.service';
import { TooltipService } from '../../_services/tooltip.service';
import { CommonModule } from '@angular/common';
import { OfferStatus } from '../../enum/offerStatus';
import { ThemeService } from '../../_services/theme.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-shopping-cart-table',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './shopping-cart-table.component.html',
    styleUrl: './shopping-cart-table.component.scss'
})
export class ShoppingCartTableComponent {

  @Input() offers: any[] = [];
  @Input() propertyId: string = '';
  @Input() offerAliasId: string = '';
  @Input() displayRemovalButtons: boolean = true;
  @Input() displayStatus: boolean = false;

  @Output() remove = new EventEmitter<{ offer: any; aliasId?: string }>();

  public OfferStatus = OfferStatus;

  private bo = inject(BreakpointObserver);
  public isHandset = false;
  public tooltipService = inject(TooltipService);

  constructor(public sharedService: SharedService,
              public shoppingCartService: ShoppingCartService,
              public themeService: ThemeService
              ){
    const sub = this.bo.observe([Breakpoints.HandsetPortrait]).subscribe(state => {
      this.isHandset = state.matches;
    });
                 
  }



  public calculateTotalQuantity(offer: any[]): number {
    return this.shoppingCartService.calculateTotalQuantity(offer);
  }

  public calculateTotalPrice(offer: any[]): number {
    return this.shoppingCartService.calculateTotalPrice(offer);
  }

  public calculateTotalTaxes(offers: any[]): number {
    return this.shoppingCartService.calculateTotalTaxes(offers);
  }

  public calculateTotalWithTaxes(offers: any[]): number {
    return this.calculateTotalPrice(offers) + this.calculateTotalTaxes(offers);
  }

  public calculateQuantity(offer: any): number {
    return this.shoppingCartService.calculateQuantity(offer);
  }

  public calculatePrice(offer: any): number {
    return this.shoppingCartService.calculatePrice(offer);
  }

  removeOfferFromCart(offer: any, aliasId?: string): void {
    this.remove.emit({ offer, aliasId });
  }

  get sortedOffers(): any[] {
    return this.sortOffersByPriceType(this.offers);
  }

  getUpgradeItemPrice(offer: any): number {
    return offer.ofr_price || 0;
  }

  getOfferDetails(offer: any): string {
    const category = (offer?.ofr_category || '').toString().toUpperCase();
    const price = this.getUpgradeItemPrice(offer);
    const quantity = this.getOfferQuantity(offer);
    const priceText = this.formatUsd(price);

    if (category === 'ROOM') {
      return `${priceText} Per Night`;
    }

    if (category === 'ECI' || category === 'LCO') {
      return `${priceText} One Time`;
    }

    if (category === 'ANCILLARY') {
      if (quantity === 1) {
        return `${priceText} One Time`;
      }

      return `${priceText} Per Night`;
    }

    return `${priceText} One Time`;
  }

  private getOfferQuantity(offer: any): number {
    const rawQuantity = Number(offer?.ofr_quantity);
    if (Number.isFinite(rawQuantity) && rawQuantity > 0) {
      return rawQuantity;
    }

    const cartQuantity = Number(this.calculateQuantity(offer));
    if (Number.isFinite(cartQuantity) && cartQuantity > 0) {
      return cartQuantity;
    }

    return 1;
  }

  private formatUsd(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  private sortOffersByPriceType(offers: any[]): any[] {
    // Group rows by pricing model so checkout reads consistently.
    return [...(offers || [])].sort((a, b) => {
      const rankA = this.getPriceTypeRank(a);
      const rankB = this.getPriceTypeRank(b);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      // Secondary sort: highest price first within the same pricing model.
      const priceA = Number(this.getUpgradeItemPrice(a)) || 0;
      const priceB = Number(this.getUpgradeItemPrice(b)) || 0;
      return priceB - priceA;
    });
  }

  private getPriceTypeRank(offer: any): number {
    const details = this.getOfferDetails(offer).toLowerCase();

    if (details.includes('one time')) {
      return 1;
    }

    if (details.includes('per night')) {
      return 0;
    }

    return 2;
  }

}


